"use client";

/**
 * Phone input powered by intl-tel-input (vanilla JS).
 *
 * All intl-tel-input imports happen inside useEffect so they NEVER execute on
 * the server — this is the only guaranteed fix for "Internal Server Error"
 * caused by the library touching browser APIs (document, window, etc.) at
 * module-evaluation time.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { Iti } from "intl-tel-input";
// CSS imports are fine at module-level — they are bundled by webpack and
// never executed as JavaScript, so they do NOT cause SSR errors.
import "intl-tel-input/dist/css/intlTelInput.css";
import "./contact-iti-flags.css";

export type ContactPhoneItiHandle = {
  validate: () => boolean;
  /** Digits only (no +), for legacy callers */
  getMobileDigits: () => string;
  /** E.164 format, e.g. +971501234567 */
  getE164Number: () => string;
  clear: () => void;
};

type Props = { hasError?: boolean; onChange?: () => void };

export const ContactPhoneIti = forwardRef<ContactPhoneItiHandle, Props>(
  function ContactPhoneIti({ hasError, onChange }, ref) {
    const inputRef = useRef<HTMLInputElement>(null);
    const itiRef = useRef<Iti | null>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useImperativeHandle(ref, () => ({
      validate: () => {
        const iti = itiRef.current;
        if (!iti) return false;
        const digits = (iti.getNumber() ?? "").replace(/\D/g, "");
        if (!digits.length) return false;
        const ok = iti.isValidNumber();
        if (ok == null) return digits.length >= 8;
        return ok === true;
      },
      getMobileDigits: () => (itiRef.current?.getNumber() ?? "").replace(/\D/g, ""),
      getE164Number: () => {
        const raw = (itiRef.current?.getNumber() ?? "").trim();
        if (!raw) return "";
        return raw.startsWith("+") ? raw : `+${raw.replace(/\D/g, "")}`;
      },
      clear: () => {
        itiRef.current?.setNumber("");
        if (inputRef.current) inputRef.current.value = "";
      },
    }));

    useEffect(() => {
      const el = inputRef.current;
      if (!el) return;

      let iti: Iti | undefined;
      let destroyed = false;

      // Dynamic imports keep ALL of intl-tel-input out of the server bundle
      // Only the JS (browser API user) is dynamic — CSS is already static-imported above
      import("intl-tel-input/intlTelInputWithUtils").then(({ default: intlTelInput }) => {
        if (destroyed || !inputRef.current) return;

        iti = (intlTelInput as CallableFunction)(inputRef.current, {
          initialCountry: "ae",
          countrySearch: true,
          showFlags: true,
          allowDropdown: true,
          formatAsYouType: true,
          fixDropdownWidth: true,
          containerClass: "iti--inline-dropdown",
        }) as Iti;

        itiRef.current = iti;

        // Wire up the onChange callback via native DOM event
        if (onChangeRef.current) {
          inputRef.current.addEventListener("input", () => onChangeRef.current?.());
        }
      });

      return () => {
        destroyed = true;
        iti?.destroy();
        itiRef.current = null;
      };
    }, []); // run once on mount

    return (
      <div
        className={`rounded-xl border transition-colors overflow-hidden [&_.iti]:block [&_.iti]:w-full ${
          hasError
            ? "border-red-400"
            : "border-neutral-200 focus-within:border-[#2BA6E6] focus-within:ring-2 focus-within:ring-blue-50"
        }`}
      >
        <input
          ref={inputRef}
          id="floating_lead_phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          placeholder="+971 50 123 4567"
          className="w-full px-4 py-3 min-h-[48px] text-sm text-neutral-900 placeholder:text-neutral-400 bg-white border-0 outline-none focus:outline-none"
        />
      </div>
    );
  }
);
